import * as React from 'react';

import {SaveButton} from 'shells/button/save-button/save-button';
import {Card} from 'shells/card/card';
import {FormField} from 'shells/form/form-field';
import {RECIPIENT_MERGE_FIELD_OPTIONS} from 'shells/textfield/consts';
import {TextfieldWithCharacterLimit} from 'shells/textfield/textfield-with-character-limit';
import {Body} from 'shells/typography/body';

import type {EditionStatus} from 'nutshell-graphql-types';
import type {
    SmsDripEditionFragment as Edition,
    SmsEditionUpdateInput,
} from 'nutshell-graphql-types';

import {MAX_CHARACTERS, MIN_CHARACTERS} from '../../consts';
import {OptOutTextSelect} from '../../opt-out-text-select';
import type {OptOutOption} from '../../opt-out-text-select';

type Props = {
    edition: Edition;
    editionStatus: EditionStatus;
    content: string;
    isSaving: boolean;
    isFirstEdition: boolean;
    onContentChange: (content: string) => void;
    onSaveButtonClick: () => void;
    onUpdate: (input: SmsEditionUpdateInput) => void;
};

export function EditionDetailsForm(props: Props) {
    const shouldDisableSaveButton =
        props.content.length < MIN_CHARACTERS || props.content.length > MAX_CHARACTERS;

    const handleOptOutTextChange = (optOutText: OptOutOption) => {
        props.onUpdate({editionId: props.edition.id, optOutText: optOutText.value});
    };

    return (
        <Card hasBorder={true}>
            <form className='pad-16 flex flex-dir-col'>
                <FormField innerId='content' label='Text message'>
                    <TextfieldWithCharacterLimit
                        multiline={true}
                        rows={4}
                        placeholder='Type your message...'
                        maximumCharacters={MAX_CHARACTERS}
                        minimumCharacters={MIN_CHARACTERS}
                        value={props.content}
                        mergeFieldOptions={RECIPIENT_MERGE_FIELD_OPTIONS}
                        hasNoSideBorders={false}
                        onChange={props.onContentChange}
                        focusOnMount={true}
                    />
                </FormField>
                <div className='flex flex-dir-col gap-8'>
                    <OptOutTextSelect
                        isRequired={props.isFirstEdition}
                        selectedOptOutText={props.edition.optOutText}
                        onOptOutTextChange={handleOptOutTextChange}
                    />
                    {!props.isFirstEdition && (
                        <Body size='small' color='light'>
                            Opt-outs are optional for follow-up messages
                        </Body>
                    )}
                </div>
            </form>
            {props.editionStatus === 'SENDING' && (
                <div className='flex mx-16'>
                    <SaveButton
                        variant='primary'
                        buttonText='Save changes'
                        savingText='Saving'
                        successText='Changes saved'
                        isSaving={props.isSaving}
                        // @ts-expect-error
                        onClick={props.onSaveButtonClick}
                        resetOnComplete={true}
                        saveCompleteTimeout={1000}
                        isFullWidth={true}
                        justifyCenter={true}
                        size='xlarge'
                        disabled={shouldDisableSaveButton}
                    />
                </div>
            )}
        </Card>
    );
}
